/**
 * NOS-4: Inline preview render for website builder canvas.
 * Replaces iframe with native React render for instant updates and section click detection.
 * Required by NOS-5 for section context panel. NOS-90: Section catalog gap inserters.
 * NOS-92: Section drag-and-drop with dnd-kit (Spec Section 7, 20 Rule #4, #5).
 * All values from docs/BUILDER_DESIGN_SPEC.md (Sections 4, 5, 6, 7, 20).
 */
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { BlockRenderer } from "./BlockRenderer"
import { PublicNavBar } from "@/components/PublicNavBar"
import { parseAnnouncement } from "@/lib/announcement"
import { SectionGapInserter } from "./SectionGapInserter"
import type { OrgCustomizations, Organization, PageSection } from "@/templates/types"
import { siteCssVars } from "@/lib/designTokens"
import type { PageSummary } from "@/stores/customizationStore"
import type { NavItem } from "@/types/navigation"
import { useSiteStyles, FONT_SIZE_MAP } from "@/hooks/useGoogleFont"
import { getSectionStyle } from "./sectionStyles"
import { getSectionLabel } from "./sectionLabels"

/** Hero and Footer are locked (Spec Section 20 Rule #5) */
const LOCKED_SECTION_TYPES = ["hero", "footer"] as const

/** NOS-92: Sortable section wrapper. Drag handle ONLY gets listeners (Rule #4: click opens panel). */
function SortableSection({
  section,
  isLocked,
  primaryColor,
  customizations,
  org,
  selectedSectionId,
  selectedSectionIds,
  onSectionClick,
  isHomePage,
}: {
  section: PageSection
  isLocked: boolean
  primaryColor: string
  customizations: OrgCustomizations
  org: Organization | null
  selectedSectionId: string | null
  selectedSectionIds?: string[]
  onSectionClick: (sectionId: string, e: React.MouseEvent) => void
  /** NOS-236: hero scroll-arrow default (on for homepage). */
  isHomePage?: boolean
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id, disabled: isLocked })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const content = (section.content as Record<string, unknown>) ?? null
  const isSelected = selectedSectionId === section.id
  const isMultiSelected = selectedSectionIds?.includes(section.id) ?? false
  const sectionStyle = getSectionStyle()
  const outlineColor = isMultiSelected
    ? "#f59e0b"
    : section.section_type === "donation_cta" || section.section_type === "hero"
      ? "#fff"
      : primaryColor

  return (
    <div ref={setNodeRef} style={style} className="drag-zone" data-dragging={isDragging}>
      {/* Drag handle - Spec Section 7. ONLY handle gets listeners (Rule #4). */}
      {!isLocked && (
        <div
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder section"
          style={{
            position: "absolute",
            left: -30,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#fff",
            fontSize: 14,
            cursor: "grab",
            opacity: isDragging ? 1 : 0.5,
            transition: "opacity 0.15s",
            zIndex: 20,
            userSelect: "none",
            fontFamily: "'DM Sans', sans-serif",
          }}
          className="drag-h"
        >
          ⠿
        </div>
      )}

      <div
        role="button"
        tabIndex={0}
        onClick={(e) => onSectionClick(section.id, e)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ")
            onSectionClick(section.id, e as unknown as React.MouseEvent)
        }}
        style={{
          ...sectionStyle,
          outline: isSelected || isMultiSelected ? `2px solid ${outlineColor}` : "none",
          cursor: "pointer",
          opacity: isDragging ? 0.5 : 1,
          boxShadow: isDragging ? "0 20px 60px rgba(0, 0, 0, 0.5)" : undefined,
        }}
      >
        {isSelected && (
          <div
            style={{
              position: "absolute",
              top: 10,
              left: 10,
              padding: "3px 8px",
              background:
                section.section_type === "donation_cta" || section.section_type === "hero"
                  ? "#fff"
                  : primaryColor,
              color:
                section.section_type === "donation_cta" || section.section_type === "hero"
                  ? primaryColor
                  : "#fff",
              fontSize: 9,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              borderRadius: 5,
              zIndex: 10,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {getSectionLabel(section.section_type, content?._name as string | undefined)}
          </div>
        )}
        {/* NOS-116: Import/AI-generated badge — builder only */}
        {(content?.import_source === "import" || content?.import_source === "ai_generated") && (
          <div
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              padding: "3px 8px",
              background: content.import_source === "import" ? "#d97706" : "#7c3aed",
              color: "#fff",
              fontSize: 9,
              fontWeight: 600,
              borderRadius: 5,
              zIndex: 10,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {content.import_source === "import"
              ? "Imported — verify content"
              : "AI-generated — review before publishing"}
          </div>
        )}
        <BlockRenderer
          section={section}
          customizations={customizations}
          org={org}
          inBuilder
          isHomePage={isHomePage}
        />
      </div>
    </div>
  )
}

export interface InlinePreviewProps {
  org: Organization | null
  customizations: OrgCustomizations | null
  /** Sections highlighted for multi-select merge */
  selectedSectionIds?: string[]
  sections: PageSection[]
  pages?: PageSummary[]
  /** When present, used for nav links; otherwise falls back to pages */
  navItems?: NavItem[]
  selectedSectionId: string | null
  onSectionClick: (sectionId: string, e: React.MouseEvent) => void
  /** NOS-90: When provided, show gap inserters and enable add section */
  onAddSection?: (sectionType: string, afterIndex: number) => Promise<string | null>
  onSelectSection?: (sectionId: string) => void
  /** NOS-92: When provided, enable section drag-and-drop. Pass new order to persist. */
  onReorderSections?: (newOrder: PageSection[]) => Promise<void>
  /** Called when Add Section catalog opens; use to close other panels (section, brand, nav) */
  onCatalogOpen?: () => void
  /** When true, Add Section catalog should close (another panel is open) */
  catalogShouldClose?: boolean
  /** When true (builder mobile viewport), force hamburger nav */
  isMobileViewport?: boolean
  /** NOS-236: whether the previewed page is the homepage (hero scroll-arrow default). */
  isHomePage?: boolean
}

export function InlinePreview({
  org,
  customizations,
  sections,
  pages = [],
  navItems = [],
  selectedSectionId,
  selectedSectionIds = [],
  onSectionClick,
  onAddSection,
  onSelectSection,
  onReorderSections,
  onCatalogOpen,
  catalogShouldClose,
  isMobileViewport = false,
  isHomePage,
}: InlinePreviewProps) {
  const bodyFont = customizations?.body_font || customizations?.font_family || "Inter, sans-serif"
  const headingFont = customizations?.heading_font || bodyFont
  const fontFamily = bodyFont
  const primaryColor = customizations?.primary_color ?? "#3b82f6"
  const secondaryColor = customizations?.secondary_color ?? "#1e40af"
  const fontSizeBase = FONT_SIZE_MAP[customizations?.font_size ?? "medium"] ?? "16px"

  useSiteStyles(headingFont, bodyFont, customizations?.font_size ?? "medium")

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id || !onReorderSections) return
    const sorted = [...sections].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    const fromIdx = sorted.findIndex((s) => s.id === active.id)
    const toIdx = sorted.findIndex((s) => s.id === over.id)
    if (fromIdx === -1 || toIdx === -1) return
    let newOrder = arrayMove(sorted, fromIdx, toIdx)
    const hero = newOrder.find((s) => s.section_type === "hero")
    const footer = newOrder.find((s) => s.section_type === "footer")
    const middle = newOrder.filter(
      (s) => !LOCKED_SECTION_TYPES.includes(s.section_type as (typeof LOCKED_SECTION_TYPES)[number])
    )
    newOrder = [...(hero ? [hero] : []), ...middle, ...(footer ? [footer] : [])]
    onReorderSections(newOrder)
  }

  // CSS isolation: prevent builder DM Sans from bleeding into preview.
  // siteCssVars emits the full --nos-* token set (NOS-150); the explicit legacy
  // vars below keep this component's prop-based fallbacks authoritative.
  const cssVars = {
    ...siteCssVars(customizations),
    "--primary-color": primaryColor,
    "--secondary-color": secondaryColor,
    "--font-family": fontFamily,
    "--heading-font": headingFont,
    "--font-size-base": fontSizeBase,
  } as React.CSSProperties

  const visibleSections = sections.filter((s) => s.is_visible !== false)
  const sortedSections = [...visibleSections].sort(
    (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)
  )

  if (!customizations) {
    return (
      <div
        style={{
          padding: 48,
          color: "#999",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 14,
        }}
      >
        Loading…
      </div>
    )
  }

  return (
    <div
      className="nos-site-root"
      style={{
        all: "initial",
        fontFamily,
        fontSize: fontSizeBase,
        display: "block",
        background: "#ffffff",
        ...cssVars,
      }}
    >
      <PublicNavBar
        orgName={org?.name ?? "Organization"}
        logoUrl={org?.logo_url}
        navItems={navItems}
        pages={pages}
        primaryColor={primaryColor}
        navCtaText={customizations?.nav_cta_text ?? "Donate"}
        announcement={parseAnnouncement(customizations?.announcement)}
        fontFamily={fontFamily}
        isMobileViewport={isMobileViewport}
        selectable
        selectedSectionId={selectedSectionId}
        onSectionClick={onSectionClick}
        logoScale={(customizations?.logo_scale as number | undefined) ?? 1.0}
      />

      {/* Sections with gap inserters (NOS-90) and drag zones. NOS-92: DndContext when onReorderSections. */}
      {onReorderSections ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortedSections.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {sortedSections.map((section) => {
              const fullIdx = sections.findIndex((s) => s.id === section.id)
              const isLocked = LOCKED_SECTION_TYPES.includes(
                section.section_type as (typeof LOCKED_SECTION_TYPES)[number]
              )
              return (
                <div key={section.id} style={{ position: "relative" }}>
                  {onAddSection && onSelectSection && (
                    <SectionGapInserter
                      afterIndex={fullIdx - 1}
                      sections={sections}
                      primaryColor={primaryColor}
                      onAddSection={onAddSection}
                      onSelectSection={onSelectSection}
                      onCatalogOpen={onCatalogOpen}
                      catalogShouldClose={catalogShouldClose}
                    />
                  )}
                  <SortableSection
                    section={section}
                    isLocked={isLocked}
                    primaryColor={primaryColor}
                    customizations={customizations}
                    org={org}
                    selectedSectionId={selectedSectionId}
                    selectedSectionIds={selectedSectionIds}
                    onSectionClick={onSectionClick}
                    isHomePage={isHomePage}
                  />
                </div>
              )
            })}
          </SortableContext>
        </DndContext>
      ) : (
        sortedSections.map((section) => {
          const isSelected = selectedSectionId === section.id
          const content = (section.content as Record<string, unknown>) ?? null
          const sectionStyle = getSectionStyle()
          const outlineColor =
            section.section_type === "donation_cta" || section.section_type === "hero"
              ? "#fff"
              : primaryColor

          const fullIdx = sections.findIndex((s) => s.id === section.id)
          return (
            <div key={section.id} style={{ position: "relative" }}>
              {onAddSection && onSelectSection && (
                <SectionGapInserter
                  afterIndex={fullIdx - 1}
                  sections={sections}
                  primaryColor={primaryColor}
                  onAddSection={onAddSection}
                  onSelectSection={onSelectSection}
                  onCatalogOpen={onCatalogOpen}
                  catalogShouldClose={catalogShouldClose}
                />
              )}
              <div className="drag-zone" style={{ position: "relative" }}>
                <div
                  style={{
                    position: "absolute",
                    left: -30,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#fff",
                    fontSize: 14,
                    cursor: "grab",
                    opacity: 0,
                    transition: "opacity 0.15s",
                    zIndex: 20,
                    userSelect: "none",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                  className="drag-h"
                >
                  ⠿
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => onSectionClick(section.id, e)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ")
                      onSectionClick(section.id, e as unknown as React.MouseEvent)
                  }}
                  style={{
                    ...sectionStyle,
                    outline: isSelected ? `2px solid ${outlineColor}` : "none",
                    cursor: "pointer",
                  }}
                >
                  {isSelected && (
                    <div
                      style={{
                        position: "absolute",
                        top: 10,
                        left: 10,
                        padding: "3px 8px",
                        background:
                          section.section_type === "donation_cta" || section.section_type === "hero"
                            ? "#fff"
                            : primaryColor,
                        color:
                          section.section_type === "donation_cta" || section.section_type === "hero"
                            ? primaryColor
                            : "#fff",
                        fontSize: 9,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        borderRadius: 5,
                        zIndex: 10,
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      {getSectionLabel(section.section_type, content?._name as string | undefined)}
                    </div>
                  )}
                  {/* NOS-116: Import/AI-generated badge — builder only */}
                  {(content?.import_source === "import" ||
                    content?.import_source === "ai_generated") && (
                    <div
                      style={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        padding: "3px 8px",
                        background:
                          content.import_source === "import" ? "#d97706" : "#7c3aed",
                        color: "#fff",
                        fontSize: 9,
                        fontWeight: 600,
                        borderRadius: 5,
                        zIndex: 10,
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      {content.import_source === "import"
                        ? "Imported — verify content"
                        : "AI-generated — review before publishing"}
                    </div>
                  )}
                  <BlockRenderer
                    section={section}
                    customizations={customizations}
                    org={org}
                    inBuilder
                    isHomePage={isHomePage}
                  />
                </div>
              </div>
            </div>
          )
        })
      )}
      {/* Gap inserter after last section */}
      {onAddSection && onSelectSection && sortedSections.length > 0 && (() => {
        const lastSection = sortedSections[sortedSections.length - 1]
        const lastFullIdx = sections.findIndex((s) => s.id === lastSection?.id)
        return (
        <SectionGapInserter
          afterIndex={lastFullIdx >= 0 ? lastFullIdx : sections.length - 1}
          sections={sections}
          primaryColor={primaryColor}
          onAddSection={onAddSection}
          onSelectSection={onSelectSection}
          onCatalogOpen={onCatalogOpen}
          catalogShouldClose={catalogShouldClose}
        />
        )
      })()}

      {/* Drag zone hover styles - must be in same tree for :hover to work on parent */}
      <style>{`
        .drag-zone:hover .drag-h {
          opacity: 1 !important;
        }
        ${customizations?.custom_css ?? ""}
      `}</style>
    </div>
  )
}
